import CoreBase from '../../services/engines/CoreBase';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import { publish } from '../../services/utils/pubsub';
import jxWindowCache from '../JxWindowManager/JxWindowCache';
import windowPositionPublisher from '../JxWindowManager/windowPositionPublisher';
import { JxWindowEvents } from '../JxWindowManager/JxWindowEvents';
import { WindowDisplayStates } from '../JxWindowManager/Types/WindowDisplayStates';

const _name = 'JxDockEngine';

/**
 * Tab-docking of JxForm windows into a host window.
 * Persisted: dockHostId/dockIndex on WindowGeoObj (jxWindowCache).
 * Runtime: mounted panels, host slot elements, active tab, drag hover target.
 */
class JxDockEngine extends CoreBase {
    _name = _name;
    _panels = new Map(); // id → { title }
    _slots = new Map(); // hostId → { bodyEl, actionsEl }
    _active = new Map(); // hostId → active panel id
    _hoverId = null; // host under the cursor while dragging
    _hoverSourceId = null; // window being dragged over that host

    registerPanel = (id, title) => {
        this._panels.set(id, { title });
        this._notify();
    };

    unregisterPanel = (id) => {
        this._panels.delete(id);
        this._slots.delete(id);
        this._notify();
    };

    /**
     * Stores a host DOM slot (bodyEl / actionsEl). Notifies only on a real change.
     * @param {string} hostId
     * @param {'bodyEl'|'actionsEl'} key
     * @param {HTMLElement|null} el
     */
    setSlot = (hostId, key, el) => {
        const slot = this._slots.get(hostId) ?? {};
        if (slot[key] === el) return;
        this._slots.set(hostId, { ...slot, [key]: el });
        this._notify();
    };

    getSlot = (hostId) => this._slots.get(hostId) ?? null;

    getTitle = (id) => this._panels.get(id)?.title ?? id;

    // Live title (e.g. with a counter) — notifies only on a real change
    setTitle = (id, title) => {
        const panel = this._panels.get(id);
        if (!panel || panel.title === title) return;
        this._panels.set(id, { ...panel, title });
        this._notify();
    };

    getPanelIds = () => [...this._panels.keys()];

    /**
     * Host of a docked panel — null when floating, or when the host isn't rendering
     * (unmounted, closed, other tab) so the panel floats instead of vanishing.
     * A minimized host keeps its tabs: they hide with it and come back on restore.
     * @param {string} id
     * @returns {string|null}
     */
    getHostOf = (id) => {
        const hostId = jxWindowCache.getByWindowId(id)?.dockHostId;
        if (!hostId) return null;
        if (!this._panels.has(hostId)) return null;
        if (this._isMinimized(hostId)) return hostId;
        if (!this._slots.get(hostId)?.bodyEl) return null;
        return hostId;
    };

    /**
     * Tab ids of a host, host itself first, then docked panels in dock order.
     * @param {string} hostId
     * @returns {string[]}
     */
    getTabs = (hostId) => {
        const docked = [...this._panels.keys()].filter((id) => this.getHostOf(id) === hostId);
        docked.sort((a, b) => this._dockIndex(a) - this._dockIndex(b));
        return [hostId, ...docked];
    };

    hasTabs = (id) => this.getTabs(id).length > 1;

    getActive = (hostId) => {
        const id = this._active.get(hostId);
        if (!id) return hostId;
        if (!this.getTabs(hostId).includes(id)) return hostId;
        return id;
    };

    setActive = (hostId, id) => {
        this._active.set(hostId, id);
        this._notify();
    };

    dock = (hostId, id) => {
        const index = this._nextIndex(this.getTabs(hostId));
        const geo = jxWindowCache.setByWindowId(id, { dockHostId: hostId, dockIndex: index });
        windowPositionPublisher.publish(geo);
        this._active.set(hostId, id);
        this._notify();
    };

    /**
     * Makes a docked panel float again, pos = where the tab was dropped.
     * @param {string} id
     * @param {{left: number, top: number}} pos
     */
    undock = (id, pos) => {
        const hostId = this.getHostOf(id);
        const geo = jxWindowCache.setByWindowId(id, { dockHostId: null, dockIndex: 0 });
        windowPositionPublisher.publish(geo);
        if (this._active.get(hostId) === id) this._active.delete(hostId);
        this._notify({ undocked: { id, pos } });
    };

    // Clicks inside a docked body bring its host to front
    selectHost = (hostId) => {
        if (!hostId) return;
        publish(JxWindowEvents.channel, { name: JxWindowEvents.onWindowSelected, target: hostId, data: null });
    };

    getHover = () => this._hoverId;

    getHoverSource = () => this._hoverSourceId;

    /**
     * Drop feedback while dragging — target host gets highlighted, dragged window goes translucent.
     * @param {string|null} targetId
     * @param {string|null} sourceId
     */
    setHover = (targetId, sourceId) => {
        if (this._hoverId === targetId && this._hoverSourceId === sourceId) return;
        this._hoverId = targetId;
        this._hoverSourceId = sourceId;
        this._notify();
    };

    _dockIndex = (id) => jxWindowCache.getByWindowId(id)?.dockIndex ?? 0;

    _isMinimized = (id) => jxWindowCache.getByWindowId(id)?.displayState === WindowDisplayStates.Minimized;

    _nextIndex = (tabs) => Math.max(0, ...tabs.map(this._dockIndex)) + 1;

    _notify = (data = null) => {
        ComponentNotifier.notify(_name, 'dockChanged', data);
    };
}

const jxDockEngine = new JxDockEngine();
export default jxDockEngine;
